<template>
  <div class="manager-company-patches-ui">
    <EditableList
      :list="patches"
      :pagination="pagination"
      :is-loading="isLoading"
      :has-error="hasError"
      variation-class-name="manager-patches"
      @edit="editPatch"
      @delete="deletePatch"
      @fetch="fetchPatches"
    >
      <template #header>
        <div class="manager-company-patches-ui__header">
          <FilterInput v-model="searchQuery" label="Search" variant-class-name="slim" />
          <div class="manager-company-patches-ui__form">
            <ManagerPatchForm
              :company-id="companyId"
              form-feedback-variation-class-name="no-margin"
              @upsert="onPatchAdd"
            />
            <VBtn
              class="full-width slim danger"
              :disabled="!patches.length || isDeletingAll"
              @click="showDeleteAllModal"
            >
              <i class="fr fr-close" /> All
            </VBtn>
          </div>
        </div>
      </template>
      <template #table-header>
        <VTableHeaderSortable
          :titles="TABLE_HEADERS"
          variation-class-name="manager-patches"
          @sort="onSort"
        />
      </template>
      <template #row="{ item }">
        <EditableListText class="slim">
          {{ item.name }}
        </EditableListText>
        <EditableListText class="slim" :title="getAreasLabel(item.areas)" multi-line>
          <p v-if="item.areas.length" class="manager-company-patches-ui__areas-total">
            Total areas: {{ item.areas.length }}
          </p>
          {{ getAreasLabel(item.areas) }}</EditableListText
        >
        <EditableListText class="slim">
          {{ item.type }}
        </EditableListText>
        <EditableListText class="slim">
          {{ item.users_count }}
        </EditableListText>
      </template>
    </EditableList>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { PropType } from 'vue';
  import type {
    TPaginatedCompanyPatches,
    TManagerCompanyPatchesPayload,
  } from '~/modules/manager/types';
  import type { TPatch, TPatchArea } from '~/misc/patch/types';
  import type { TSortingState } from '~/misc/v-table-header-sortable/types';
  import type { TPagination } from '~/misc/resource/types';

  // Config
  import { TABLE_HEADERS } from '~/modules/manager/company/patches/config';

  // Services
  import { PaginationCalculator } from '~/misc/pagination/services/pagination-calculator';

  // Models
  import { ManagerCompanyModel } from '~/modules/manager/models/company.model';
  import { ManagerPatchesModel } from '~/modules/manager/models/patches.model';

  // Composables
  import { useNotifyResource } from '~/misc/resource/composables/use-notify-resource';

  // Components
  import ManagerPatchForm from '~/modules/manager/patch/form/ManagerPatchForm.vue';
  import EditableList from '~/misc/editable-list/EditableList.vue';
  import EditableListText from '~/misc/editable-list/text/EditableListText.vue';
  import VTableHeaderSortable from '~/misc/v-table-header-sortable/VTableHeaderSortable.vue';
  import VBtn from '~/misc/v-btn';
  import FilterInput from '~/misc/filter-input/FilterInput.vue';

  const UpdateModal = defineAsyncComponent(
    () => import('~/modules/manager/patch/update-modal/ManagerPatchUpdateModal.vue')
  );

  const DeleteAllModal = defineAsyncComponent(
    () => import('~/modules/manager/company/delete-modal/ManagerCompanyDeleteModal.vue')
  );

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    companyName: {
      type: String as PropType<string | null>,
      default: null,
    },
  });

  const managerCompanyModel: ManagerCompanyModel = new ManagerCompanyModel();
  const managerPatchesModel: ManagerPatchesModel = new ManagerPatchesModel();

  const { show: showModal } = useModal();
  const { notifySuccess, notifyFailure } = useNotifyResource();

  const searchQuery: Ref<string> = ref<string>('');
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const hasError: Ref<boolean> = ref<boolean>(false);
  const isDeletingAll: Ref<boolean> = ref<boolean>(false);

  const patches: Ref<TPatch[]> = ref<TPatch[]>([]);
  const payload: Ref<TManagerCompanyPatchesPayload> = ref<TManagerCompanyPatchesPayload>({});
  const pagination: Ref<TPagination | null> = ref<TPagination | null>(null);

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setHasError = (state: boolean) => {
    hasError.value = state;
  };

  const setIsDeletingAll = (state: boolean) => {
    isDeletingAll.value = state;
  };

  const setPayload = (state: TManagerCompanyPatchesPayload) => {
    payload.value = state;
  };

  const setPatchesData = (data: TPatch[], meta: TPagination | null) => {
    patches.value = data;
    pagination.value = meta;
  };

  const getAreasLabel = (areas: TPatchArea[]): string =>
    areas.length ? areas.map((area: TPatchArea) => area.value).join(', ') : '-';

  const findPatchIndex = (id: number) =>
    patches.value.findIndex((existingPatch: TPatch) => existingPatch.id === id);

  const onPatchAdd = async () => {
    const pageToFetch: number = PaginationCalculator.getPageOnAdd(pagination.value);

    await fetchPatches(pageToFetch);
  };

  const editPatch = (patch: TPatch) => {
    showModal(UpdateModal, {
      patch,
      updatedCallback: (updatedPatch: TPatch) => {
        const index: number = findPatchIndex(updatedPatch.id);

        if (index !== -1) {
          patches.value[index] = updatedPatch;

          notifySuccess();
        }
      },
    });
  };

  const handlePatchDelete = async (id: number) => {
    await managerPatchesModel.destroy(id).catch((e) => {
      const message: string =
        e.response?.status === 404
          ? "Sorry, patch doesn't exist"
          : 'Something went wrong while deleting patch';

      throw new Error(message);
    });
  };

  const deletePatch = async (id: number) => {
    await handlePatchDelete(id);

    const pageToFetch: number = PaginationCalculator.getPageOnDelete(pagination.value);

    await fetchPatches(pageToFetch);

    notifySuccess();
  };

  const deleteAllPatches = async () => {
    if (!props.companyId) return;

    try {
      setIsDeletingAll(true);
      await managerCompanyModel.destroyPatches(props.companyId);
      setPatchesData([], null);
      notifySuccess();
    } catch {
      notifyFailure();
    } finally {
      setIsDeletingAll(false);
    }
  };

  const showDeleteAllModal = () => {
    showModal(DeleteAllModal, {
      confirmedCallback: deleteAllPatches,
      companyName: props.companyName,
      title: 'Delete all patches',
      confirmBtnText: 'Delete All',
    });
  };

  const fetchPatches = async (page: number = 1) => {
    if (!props.companyId) return;

    try {
      setIsLoading(true);
      setHasError(false);

      const response: TPaginatedCompanyPatches = await managerCompanyModel.patches(
        props.companyId,
        {
          page,
          q: searchQuery.value || undefined,
          ...payload.value,
        }
      );

      setPatchesData(response.data, response.meta);
    } catch (e) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onSort = (value: TSortingState | null) => {
    setPayload(value ? { sorting: value } : {});
    fetchPatches(1);
  };

  fetchPatches();

  watch(searchQuery, () => fetchPatches(), { immediate: true });
</script>
